'use client';

import React, { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import CTACard from '@/components/common/CTACard';
import { marked } from 'marked';
import type { ContentPage } from '@/lib/supabase';
import { extractFaqs, buildFaqJsonLd } from '@/lib/extractFaqs';
import { processHtml } from '@/lib/htmlPostProcess';
import { buildHreflangLinks } from '@/lib/hreflang';

interface ComparisonLayoutProps {
  page: ContentPage;
  localeVariants?: { slug: string }[];
}

const ComparisonLayout: React.FC<ComparisonLayoutProps> = ({ page, localeVariants = [] }) => {
  const contentHtml = useMemo(
    () => processHtml(marked.parse(page.body_markdown) as string),
    [page.body_markdown]
  );

  const competitor = page.slug.replace('vs/', '');
  const competitorName = competitor.charAt(0).toUpperCase() + competitor.slice(1);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': page.schema_type || 'WebPage',
    name: page.title,
    description: page.description,
    url: `https://intelgrader.com/${page.slug}`,
    author: {
      '@type': 'Person',
      name: page.author || 'IntelGrader Team',
      worksFor: { '@type': 'Organization', name: 'IntelGrader', url: 'https://intelgrader.com' },
    },
    publisher: { '@type': 'Organization', name: 'IntelGrader', url: 'https://intelgrader.com', logo: { '@type': 'ImageObject', url: 'https://intelgrader.com/assets/logo-wordmark.png' } },
    dateModified: page.updated_at,
  }), [page]);

  const breadcrumbLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://intelgrader.com/' },
      { '@type': 'ListItem', position: 2, name: 'Comparisons', item: 'https://intelgrader.com/vs' },
      { '@type': 'ListItem', position: 3, name: `vs ${competitorName}`, item: `https://intelgrader.com/${page.slug}` },
    ],
  }), [page, competitorName]);

  const faqJsonLd = useMemo(() => {
    const faqs = extractFaqs(page.body_markdown);
    return buildFaqJsonLd(faqs);
  }, [page.body_markdown]);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}

      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        {/* Breadcrumb */}
        <nav className="text-sm text-[var(--color-muted)] mb-8">
          <Link href="/" className="hover:text-[var(--color-primary)]">Home</Link>
          <span className="mx-2">/</span>
          <span>vs {competitorName}</span>
        </nav>

        {/* Header */}
        <header className="mb-12">
          <div className="inline-block px-3 py-1 rounded-full bg-[var(--color-primary-subtle)] text-[var(--color-primary)] text-xs font-semibold mb-4">
            Comparison
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[var(--color-text)] tracking-tight leading-tight mb-6">
            {page.title}
          </h1>
          {page.description && (
            <p className="text-lg text-[var(--color-muted)] max-w-2xl">
              {page.description}
            </p>
          )}
        </header>

        {/* Hero image */}
        {page.image_url && (
          <Image
            src={page.image_url}
            alt={page.image_alt || page.title}
            className="w-full rounded-2xl mb-12"
            width={1200}
            height={630}
            priority
          />
        )}

        {/* Body */}
        <div
          className="prose-content"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />

        {/* CTA */}
        <CTACard
          title="Ready to see the difference?"
          description="Book a demo and see how IntelGrader's AI grading compares in action."
          variant="primary"
        />
      </div>
    </>
  );
};

export default ComparisonLayout;
